import React, { useRef } from 'react';
import { Paperclip, Plus, Trash2, Eye } from 'lucide-react';
import { FileHoverPreview } from '../MediaPreview';

const formatBytes = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * The bill, receipt or cheque photos kept with an entry. Optional, several at once. `existing`
 * are files already saved (edit mode), shown with a hover preview and a Remove; `files` are ones
 * picked now and not yet uploaded.
 */
const AttachmentPicker = ({
  label = 'Bill or receipt photo',
  hint = 'Optional. Photos or PDFs, up to 5 MB each.',
  files = [],
  onFiles,
  existing = [],
  onRemoveExisting,
  fileUrl,
}) => {
  const inputRef = useRef(null);
  const total = existing.length + files.length;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <label className="block text-sm font-medium text-gray-700">
          {label}
          <span className="ml-2 font-normal text-gray-400 text-xs">{hint}</span>
        </label>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-gray-50 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <Plus className="w-4 h-4" /> Add photo
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,application/pdf"
        className="hidden"
        data-no-dirty
        onChange={(e) => {
          const picked = Array.from(e.target.files || []);
          if (picked.length) onFiles([...files, ...picked]);
          // Cleared so picking the same file again still fires a change.
          e.target.value = '';
        }}
      />
      {total > 0 && (
        <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
          {existing.map(a => (
            <div key={a.id} className="flex items-center gap-3 px-3 py-2 bg-white">
              <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <FileHoverPreview
                url={fileUrl ? fileUrl(a.id) : null}
                label={a.originalFileName || 'Attachment'}
                isPdf={(a.contentType || '').includes('pdf')}
                className="flex-1 min-w-0 inline-flex"
              >
                {({ href }) => (
                  <a href={href} target="_blank" rel="noreferrer" className="min-w-0 inline-flex items-center gap-1.5" title={`${a.originalFileName || 'Attachment'}: opens in a new tab`}>
                    <Eye className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate text-sm text-emerald-700 hover:underline">
                      {a.originalFileName || 'Attachment'}
                    </span>
                  </a>
                )}
              </FileHoverPreview>
              <span className="text-xs text-gray-400 flex-shrink-0">{formatBytes(a.sizeBytes)}</span>
              {onRemoveExisting && (
                <button
                  type="button"
                  onClick={() => onRemoveExisting(a)}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg flex-shrink-0"
                  title="Remove this file"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          {files.map((f, i) => (
            <div key={`new-${i}`} className="flex items-center gap-3 px-3 py-2 bg-emerald-50/40">
              <Paperclip className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <span className="flex-1 min-w-0 truncate text-sm text-gray-800" title={f.name}>{f.name}</span>
              <span className="text-xs text-gray-400 flex-shrink-0">{formatBytes(f.size)}</span>
              <button
                type="button"
                onClick={() => onFiles(files.filter((_, k) => k !== i))}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg flex-shrink-0"
                title="Remove this file"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AttachmentPicker;
